import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { brandInfo, brandValues, milestones, honors, contactInfo } from '@/mocks/brand';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于呦呦',
  description: brandInfo.description,
  url: 'https://yooyoozone.github.io/about',
  isPartOf: { '@type': 'WebSite', name: '呦呦 YouYou' },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '呦呦品牌的核心理念是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '呦呦品牌以《诗经》为灵感之源，致力于将千年东方美学融入现代生活。我们坚持自然纯粹、文化传承、匠心品质、生态友好四大品牌价值观。',
      },
    },
    {
      '@type': 'Question',
      name: '呦呦有哪些产品系列？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '呦呦目前拥有四大产品系列：护肤系列（清露精华液、凝脂面霜等）、香氛系列（鹿鸣香薰、蒹葭蜡烛等）、茶品系列（南山白茶、幽谷乌龙等）和家居系列（幽兰茶具、清风竹编等）。',
      },
    },
    {
      '@type': 'Question',
      name: '如何联系呦呦品牌？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过以下方式联系我们：邮箱 hello@youyou.com，电话 400-888-9688，地址为浙江省杭州市西湖区龙井路18号呦呦文化创意园。工作时间为周一至周五 9:00-18:00。',
      },
    },
    {
      '@type': 'Question',
      name: '呦呦产品是否使用天然成分？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，呦呦所有产品均以天然植物为本，从《诗经》中记载的草木获取灵感，拒绝化学添加，每一款产品都是大自然的真诚馈赠。',
      },
    },
  ],
};

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl?.value.trim()) {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    const formData = new FormData(form);

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u4pcdca23r0tr1aqi0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解呦呦品牌故事——源自《诗经》的东方美学品牌。包含品牌介绍、价值理念、发展历程、品牌荣誉与联系方式。"
        canonicalUrl="https://yooyoozone.github.io/about"
        jsonLd={[aboutJsonLd, faqJsonLd]}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero */}
          <section className="relative rounded-lg overflow-hidden h-[300px] md:h-[400px] mb-16">
            <img
              src="https://readdy.ai/api/search-image?query=A%20serene%20oriental%20brand%20studio%20space%20with%20warm%20natural%20lighting%2C%20wooden%20shelves%20with%20ceramic%20objects%20and%20plants%2C%20minimalist%20aesthetic%2C%20cream%20walls%2C%20jade%20green%20accents%2C%20artistic%20atmosphere%2C%20zen%20simplicity&width=1600&height=800&seq=youyou-about-hero&orientation=landscape"
              alt="关于呦呦"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-background-50">
                关于呦呦
              </h1>
              <p className="mt-2 text-sm text-background-200/90">
                {brandInfo.tagline}
              </p>
            </div>
          </section>

          {/* Brand Intro */}
          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                  品牌故事
                </h2>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  {brandInfo.description}
                </p>
                <div className="mt-6 flex flex-wrap gap-6 text-sm">
                  <div>
                    <span className="text-foreground-400">创立时间</span>
                    <p className="font-medium text-foreground-800 mt-1">{brandInfo.founded}</p>
                  </div>
                  <div>
                    <span className="text-foreground-400">总部所在地</span>
                    <p className="font-medium text-foreground-800 mt-1">{brandInfo.location}</p>
                  </div>
                </div>
              </div>
              <div className="h-64 md:h-80 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Oriental%20brand%20team%20working%20in%20a%20minimalist%20studio%2C%20natural%20materials%20and%20products%20on%20wooden%20table%2C%20warm%20natural%20lighting%2C%20cream%20walls%2C%20creative%20atmosphere&width=800&height=600&seq=youyou-about-story&orientation=landscape"
                  alt="呦呦品牌故事"
                  title="呦呦品牌 品牌故事"
                  className="w-full h-full object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="mb-16 py-12 bg-background-100 rounded-lg px-6 md:px-10">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 text-center mb-10">
              品牌价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {brandValues.map((v, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${v.icon} text-xl text-primary-600`} />
                  </div>
                  <h3 className="mt-3 font-heading text-sm font-semibold text-foreground-900">{v.title}</h3>
                  <p className="mt-2 text-xs text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Milestones */}
          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-10">
              品牌历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px" />
              <div className="space-y-8">
                {milestones.map((m, idx) => (
                  <div key={idx} className={`relative flex items-start gap-6 md:gap-0 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="absolute left-2.5 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 md:-translate-x-1.5 z-10" />
                    <div className={`pl-10 md:pl-0 md:w-1/2 ${idx % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                      <span className="font-heading text-lg font-semibold text-primary-500">{m.year}</span>
                      <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{m.event}</p>
                    </div>
                    <div className="hidden md:block md:w-1/2" />
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Honors */}
          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-8">
              品牌荣誉
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {honors.map((h, idx) => (
                <div key={idx} className="p-5 rounded-lg bg-background-50 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-3">
                    <i className="ri-award-line text-lg text-accent-600" />
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900">{h.title}</h3>
                  <p className="mt-1 text-xs text-foreground-500">{h.source}</p>
                  <span className="inline-block mt-2 text-[10px] text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full">{h.year}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">邮箱</p>
                    <p className="text-sm text-foreground-500 mt-0.5">{contactInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-phone-line text-lg text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">电话</p>
                    <p className="text-sm text-foreground-500 mt-0.5">{contactInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">地址</p>
                    <p className="text-sm text-foreground-500 mt-0.5">{contactInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-time-line text-lg text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">工作时间</p>
                    <p className="text-sm text-foreground-500 mt-0.5">{contactInfo.workHours}</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-5">发送消息</h3>
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（不超过500字）"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {submitting ? '发送中...' : '发送消息'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 text-center">消息已发送，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>

          {/* Map */}
          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
              找到我们
            </h2>
            <div className="rounded-lg overflow-hidden h-[300px] md:h-[400px]">
              <iframe
                title="呦呦品牌地址"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3401.5973508502514!2d120.13051231564084!3d30.25020028181608!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x344b6256f03603b7%3A0x16a7bc3771e3e6e4!2sWest%20Lake%2C%20Hangzhou%2C%20Zhejiang!5e0!3m2!1sen!2scn!4v1690000000000!5m2!1sen!2scn"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}